/**
 * @file autoResizeTextarea.js
 * @description 全域 Textarea 內容高度自適應工具
 */

/**
 * 依據內容動態調整指定 Textarea 高度
 * @param {Element|HTMLTextAreaElement} el - 目標 Textarea 元素
 */
export const adjustTextareaHeight = (el) => {
    try {
        if (!(el instanceof HTMLTextAreaElement)) return;
        if (!el.classList.contains('form-control')) return;

        el.style.overflowY = 'hidden';
        el.style.resize = 'none';

        // 暫時設為 auto 以正確取得最新的 scrollHeight
        el.style.height = 'auto';
        if (el.scrollHeight > 0) {
            el.style.height = `${el.scrollHeight}px`;
        }
    } catch (error) {
        console.error('[adjustTextareaHeight] 調整 Textarea 高度失敗:', error);
    }
};

/**
 * 初始化全域 Textarea 自適應高度機制
 * 涵蓋使用者輸入、Vue 響應式資料更新、DOM 新增節點與視窗縮放
 */
export const initAutoResizeTextarea = () => {
    try {
        // 1. 攔截 HTMLTextAreaElement.prototype.value 的 setter，捕捉非同步資料載入與 Vue 賦值
        const descriptor = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value');
        if (descriptor && descriptor.set) {
            const originalSet = descriptor.set;
            Object.defineProperty(HTMLTextAreaElement.prototype, 'value', {
                set(val) {
                    originalSet.call(this, val);
                    adjustTextareaHeight(this);
                }
            });
        }

        // 2. 監聽使用者打字輸入事件
        document.addEventListener('input', (event) => {
            adjustTextareaHeight(event.target);
        }, true);

        // 3. 焦點進入時校正高度（處理從 hidden / 摺疊區塊切換出來的狀況）
        document.addEventListener('focusin', (event) => {
            adjustTextareaHeight(event.target);
        }, true);

        // 4. 視窗縮放時重新計算折行換行高度
        window.addEventListener('resize', () => {
            document.querySelectorAll('textarea.form-control').forEach(adjustTextareaHeight);
        });

        // 5. 監聽 DOM 樹變更，動態節點渲染時自動調整
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        if (node.matches?.('textarea.form-control')) {
                            adjustTextareaHeight(node);
                        }
                        node.querySelectorAll?.('textarea.form-control').forEach(adjustTextareaHeight);
                    }
                });
            });
        });

        observer.observe(document.documentElement, { childList: true, subtree: true });

        // 6. 頁面載入完成時初次掃描
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                document.querySelectorAll('textarea.form-control').forEach(adjustTextareaHeight);
            });
        } else {
            document.querySelectorAll('textarea.form-control').forEach(adjustTextareaHeight);
        }
    } catch (error) {
        console.error('[initAutoResizeTextarea] 全域自適應初始化失敗:', error);
    }
};
